// BTList (레이아웃 안정화: width 100% & 툴바/검색 안 찌그러짐)
import React, { useState, useEffect, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import BTBox from '../BTBox';
import { Button, Box } from '@mui/material';
import { HorizontalLayout, VerticalLayout } from '../../common-layout';
import BTSearchBox from '../BTSearchBox';
import { BTSelectBox } from '../BTSelectBox';
import { TitleNormal } from '../../common-text';
import theme from '../../theme';
import { boardStateStore } from '../../../store/BoardStateStore';

interface BTListProps {
    rows: any[];
    columns: any[];
    handleRowClick: (params: any) => void;
    onWriteButtonClick?: () => void;
    onDeleteButtonClick?: (selectedIndexes: number[]) => void;
    checkboxSelection?: boolean;
    writeButtonText?: string;
    deleteButtonText?: string;
    selectedRows: number[];
    setSelectedRows: (indexes: number[]) => void;
    filterTitle?: string;
    filterOptions?: { label: string; value: string }[];
    filterValue?: string;
    onFilterChange?: (value: string) => void;
    style?: React.CSSProperties;
    pageStateKey?: string;
}

const BTList: React.FC<BTListProps> = ({
    rows,
    columns,
    handleRowClick,
    onWriteButtonClick,
    onDeleteButtonClick,
    checkboxSelection = false,
    writeButtonText,
    deleteButtonText,
    selectedRows,
    setSelectedRows,
    filterTitle,
    filterOptions,
    filterValue,
    onFilterChange,
    style,
    pageStateKey,
}) => {
    const [paginationModel, setPaginationModel] = useState(() => {
        if (!pageStateKey) return { page: 0, pageSize: 10 };
        const saved = boardStateStore.getBoardState(pageStateKey);
        return {
            page: saved?.page ?? 0,
            pageSize: saved?.pageSize ?? 10,
        };
    });
    const [filteredRows, setFilteredRows] = useState(rows);
    const [searchKeywords, setSearchKeywords] = useState<string[]>([]);
    const didHydrateRef = useRef(false);

    useEffect(() => {
        const filtered = rows.filter((row) => {
            const text = Object.values(row).join(' ').toLowerCase();
            return searchKeywords.every((keyword) => text.includes(keyword));
        });
        setFilteredRows(filtered);
    }, [rows, searchKeywords]);

    // 초기 렌더 시 저장된 페이지 우선 복원
    useEffect(() => {
        if (!pageStateKey) return;
        const saved = boardStateStore.getBoardState(pageStateKey);

        if (saved) {
            if (saved.searchQuery) {
                const keywords = String(saved.searchQuery).toLowerCase().split(' ').filter((w) => w.trim() !== '');
                setSearchKeywords(keywords);
            }
            if (saved.selectedFilter && onFilterChange) {
                onFilterChange(saved.selectedFilter);
            }
        }
        didHydrateRef.current = true;
    }, [pageStateKey]);

    // Persist state on changes
    useEffect(() => {
        if (!pageStateKey || !didHydrateRef.current) return;
        const nextState = {
            page: paginationModel?.page,
            pageSize: paginationModel?.pageSize,
            searchQuery: searchKeywords.join(' '),
            selectedFilter: filterValue,
        } as const;
        boardStateStore.updateBoardState(pageStateKey, nextState as any);
    }, [pageStateKey, paginationModel, searchKeywords, filterValue]);

    const handleSearch = (query: string) => {
        const keywords = query.toLowerCase().split(' ').filter((w) => w.trim() !== '');
        setSearchKeywords(keywords);

        const filtered = rows.filter((row) => {
            const rowText = Object.values(row).join(' ').toLowerCase();
            return keywords.every((k) => rowText.includes(k));
        });
        setFilteredRows(filtered);
    };

    const handleSelectionModelChange = (newSelectionModel: any) => {
        const selectedIndexes = newSelectionModel.map((selectedId: any) => {
            const selectedRow = rows.find((row) => row.id === selectedId);
            return selectedRow ? rows.indexOf(selectedRow) : -1;
        });
        setSelectedRows(selectedIndexes);
    };

    const koreanLocaleText = {
        noRowsLabel: '데이터가 없습니다',
        noResultsOverlayLabel: '결과가 없습니다',
        toolbarDensity: '밀도',
        toolbarDensityLabel: '밀도',
        toolbarDensityCompact: '좁게',
        toolbarDensityStandard: '보통',
        toolbarDensityComfortable: '넓게',
        toolbarColumns: '열',
        toolbarColumnsLabel: '열 선택',
        toolbarFilters: '필터',
        toolbarFiltersLabel: '필터 보기',
        toolbarExport: '내보내기',
        toolbarExportCSV: 'CSV로 내보내기',
        toolbarExportPrint: '프린트',
        paginationRowsPerPage: '페이지당 행 수:',
        MuiTablePagination: { labelRowsPerPage: '페이지당 행 수:' },
        columnMenuSortAsc: '오름차순 정렬',
        columnMenuSortDesc: '내림차순 정렬',
        columnMenuFilter: '필터',
        columnMenuHideColumn: '열 숨기기',
        columnMenuShowColumns: '열 보기',
        footerRowSelected: (count: number) => (count === 1 ? '1개 행 선택됨' : `${count}개 행 선택됨`),
    };

    return (
        // ⭐ CHANGED: 레이아웃 안정화용 style 추가 (flex column, flex:1, minWidth/Height 0)
        <VerticalLayout
            width="100%"
            style={{
                display: 'flex',            // ⭐ CHANGED
                flexDirection: 'column',    // ⭐ CHANGED
                flex: 1,                    // ⭐ CHANGED (부모가 flex:1로 감쌀 때 전체 차지)
                minWidth: 0,                // ⭐ CHANGED (가로 찌그러짐 방지)
                minHeight: 0,               // ⭐ CHANGED (세로 스크롤 시 패딩 사라짐 방지)
                boxSizing: 'border-box',   // ★ CHANGED: 폭 계산 안정화
                ...(style || {}),
            }}
        >
            {/* 상단 툴바 */}
            <Box
                width="100%"
                height="50px"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ minWidth: 0, boxSizing: 'border-box', pr: 1}}
            >
                <Box
                    flex={1}
                    display="flex"
                    alignItems="center"
                    sx={{ minWidth: 0, gap: 2, flexWrap: 'wrap', boxSizing: 'border-box' }}
                >
                    {filterOptions && filterTitle && onFilterChange && (
                        <HorizontalLayout alignItems="center">
                        <TitleNormal color="#8a92a6">{filterTitle}</TitleNormal>
                        <BTBox height="8px" />
                        <div style={{ width: 200, flex: '0 0 auto' }}>
                            <BTSelectBox
                            width="100%"
                            value={filterValue || ''}
                            options={filterOptions}
                            onChange={(event) => onFilterChange(event.target.value)}
                            />
                        </div>
                        </HorizontalLayout>
                    )}
                </Box>
                <Box width="300px" >
                    <BTSearchBox onSearch={handleSearch} />
                </Box>
            </Box>

            <BTBox height="16px" />

            {/* DataGrid 래퍼: flex:1 + minHeight:0 + width:100% */}
            <div
                style={{
                    minHeight: 0,      // ⭐ CHANGED: 내부 스크롤 정상 동작
                    width: '100%',     // ⭐ CHANGED
                    display: 'block',   // ⭐ CHANGED
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                }}
            >
                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    localeText={koreanLocaleText}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => setPaginationModel(model)}
                    pageSizeOptions={[5, 10, 20, 50, 100]}
                    pagination
                    disableColumnMenu
                    disableRowSelectionOnClick
                    checkboxSelection={checkboxSelection}
                    sx={{
                        '& .MuiDataGrid-row.Mui-selected': {
                            backgroundColor: '#E0FFEA !important',
                        },
                        '--DataGrid-containerBackground': '#14B446',
                        '&.MuiDataGrid-root': {
                            border: 'none',
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            height: '100%',         // ⭐ CHANGED: 부모 래퍼 높이 채우기
                            width: '100%',          // ⭐ CHANGED: 가로 100%
                            boxSizing: 'border-box',    // ★ CHANGED
                        },
                        '&.MuiDataGrid-withBorderColor': {
                            border: 'none',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            borderBottom: 'none',
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            color: '#FFFFFF',
                            fontSize: '16px',
                            fontWeight: 'medium',
                        },
                        '& .MuiDataGrid-row': {
                            borderBottom: 'none',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderTop: 'none',
                        },
                        '& .MuiDataGrid-cell': {
                            border: 'none',
                            color: '#232D42',
                            fontSize: '16px',
                        },
                        '& .MuiDataGrid-columnSeparator': {
                            color: '#14B446',
                            visibility: 'visible',
                        },
                        '& .first-column': {
                            color: '#8a92a6',
                        },
                        '& .MuiCheckbox-root': {
                            color: '#14B446 !important',
                        },
                        '& .MuiDataGrid-cell:focus': {
                            outline: 'none !important',
                        },
                        '& .MuiDataGrid-sortIcon': {
                            visibility: 'hidden',
                        },
                        // 정렬된 컬럼에서만 아이콘 표시
                        '& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-sortIcon': {
                            visibility: 'visible',
                        },
                    }}
                    getCellClassName={(params) => {
                        if (params.field === columns[0].field) {
                            return 'first-column';
                        }
                        return '';
                    }}
                    onRowClick={(params) => {
                        /// 아이템 클릭시 현재 페이지 상태 저장
                        if (pageStateKey) {
                            const clickState = {
                                page: paginationModel?.page,
                                pageSize: paginationModel?.pageSize,
                                searchQuery: searchKeywords.join(' '),
                                selectedFilter: filterValue,
                            } as const;
                            boardStateStore.updateBoardState(pageStateKey, clickState);
                        }
                        handleRowClick(params);
                    }}
                    rowSelectionModel={selectedRows
                        .map((index) => filteredRows[index]?.id)
                        .filter((id) => id !== undefined)}
                    onRowSelectionModelChange={handleSelectionModelChange}
                />
            </div>

            <BTBox height="20px" width="10px" />

            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', flexShrink: 0 /* ⭐ CHANGED */ }}>
                {onDeleteButtonClick && (
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: '#FC5A5A',
                            color: 'white',
                            fontSize: '18px',
                            fontWeight: '700',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: theme.colors.colorWarn },
                            width: '120px',
                            height: '48px',
                            '&:focus': { outline: 'none' },
                            '&:active': { outline: 'none' },
                        }}
                        onClick={() => onDeleteButtonClick(selectedRows)}
                    >
                        {deleteButtonText || '계정삭제'}
                    </Button>
                )}
                {onWriteButtonClick && (
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: theme.colors.bbTech,
                            color: 'white',
                            fontSize: '18px',
                            fontWeight: '700',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: theme.colors.bbTech },
                            width: '120px',
                            height: '48px',
                            '&:focus': { outline: 'none' },
                            '&:active': { outline: 'none' },
                        }}
                        onClick={onWriteButtonClick}
                    >
                        {writeButtonText || '추가'}
                    </Button>
                )}
            </div>

            <BTBox height="20px" width="10px" />
        </VerticalLayout>
    );
};

export default BTList;